import { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import apiClient from '../../../services/api';

const api = {
  getContexto:         ()      => apiClient.get('/plan-operativo/contexto').then(r => r.data),
  getSecciones:        ()      => apiClient.get('/plan-operativo/secciones').then(r => r.data),
  getActividades:      ()      => apiClient.get('/actividades').then(r => r.data),
  getPlanMes:          (a, m)  => apiClient.get(`/plan-operativo/plan/${a}/${m}`).then(r => r.data),
  crearPlan:           (data)  => apiClient.post('/plan-operativo/plan', data).then(r => r.data),
  editarPlan:          (id, data) => apiClient.put(`/plan-operativo/plan/${id}`, data).then(r => r.data),
  eliminarPlan:        (id)    => apiClient.delete(`/plan-operativo/plan/${id}`).then(r => r.data),
  getRegistrosFecha:   (fecha) => apiClient.get(`/plan-operativo/registros/fecha/${fecha}`).then(r => r.data),
  getRegistrosMes:     (a, m)  => apiClient.get(`/plan-operativo/registros/mes/${a}/${m}`).then(r => r.data),
  getRegistrosMesDetalle: (a, m, idCp) => apiClient.get(`/plan-operativo/registros/mes-detalle/${a}/${m}${idCp ? `?id_cp=${idCp}` : ''}`).then(r => r.data),
  crearRegistro:       (data)  => apiClient.post('/plan-operativo/registros', data).then(r => r.data),
  editarRegistro:      (id, data) => apiClient.put(`/plan-operativo/registros/${id}`, data).then(r => r.data),
  eliminarRegistro:    (id)    => apiClient.delete(`/plan-operativo/registros/${id}`).then(r => r.data),
  getResumenMes:       (a, m, idCp)  => apiClient.get(`/plan-operativo/resumen/${a}/${m}${idCp ? `?id_cp=${idCp}` : ''}`).then(r => r.data),
};

export function getTokenUser() {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return token ? jwtDecode(token) : {};
  } catch {
    return {};
  }
}

export function usePlanOperativo() {
  const [secciones,   setSecciones]   = useState([]);
  const [actividades, setActividades] = useState([]);
  const [contexto,    setContexto]    = useState(null);
  const [error,       setError]       = useState(null);

  useEffect(() => {
    api.getContexto()
      .then(ctx => setContexto(ctx))
      .catch(e => setError(`Contexto: ${e.response?.data?.error || e.message}`));

    api.getSecciones()
      .then(s => setSecciones(s))
      .catch(e => setError(`Secciones: ${e.response?.data?.error || e.message}`));

    api.getActividades()
      .then(a => setActividades(a))
      .catch(e => setError(`Actividades: ${e.response?.data?.error || e.message}`));
  }, []);

  return { secciones, actividades, contexto, error };
}

export function useCalendario(anio, mes) {
  const [diasConRegistros, setDias]    = useState({});
  const [planMes,          setPlan]    = useState([]);
  const [cargando,         setCargando] = useState(false);
  const [error,            setError]   = useState(null);

  const cargar = useCallback(async () => {
    setError(null);
    setCargando(true);
    try {
      const [registros, plan] = await Promise.all([
        api.getRegistrosMes(anio, mes),
        api.getPlanMes(anio, mes),
      ]);
      const mapa = {};
      registros.forEach(r => {
        mapa[r.fecha] = {
          total_jornales: r.total_jornales,
          colores: r.colores ? r.colores.split(',') : [],
        };
      });
      setDias(mapa);
      setPlan(plan);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, [anio, mes]);

  const guardarPlan = useCallback(async (datos) => {
    try {
      const { id, ...payload } = datos;
      if (id) {
        await api.editarPlan(id, payload);
      } else {
        await api.crearPlan(payload);
      }
      await cargar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al guardar plan');
      throw err;
    }
  }, [cargar]);

  const eliminarPlan = useCallback(async (id) => {
    try {
      await api.eliminarPlan(id);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al eliminar plan');
      throw err;
    }
  }, [cargar]);

  useEffect(() => { cargar(); }, [cargar]);

  return { diasConRegistros, planMes, recargar: cargar, guardarPlan, eliminarPlan, cargando, error };
}

export function useDia(fecha) {
  const [registros, setRegistros] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error,     setError]     = useState(null);

  const cargar = useCallback(async () => {
    if (!fecha) return;
    try {
      setError(null);
      setRegistros(await api.getRegistrosFecha(fecha));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al cargar registros');
    }
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async (datos) => {
    setGuardando(true);
    try {
      const { id, ...payload } = datos;
      if (id) {
        await api.editarRegistro(id, { ...payload, fecha });
      } else {
        await api.crearRegistro({ ...payload, fecha });
      }
      await cargar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al guardar registro');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    try {
      await api.eliminarRegistro(id);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al eliminar registro');
    }
  };

  return { registros, guardar, eliminar, guardando, error };
}

export function useResumenMes(anio, mes, idCp = null) {
  const [registros,  setRegistros]  = useState([]);
  const [resumen,    setResumen]    = useState(null);
  const [cargando,   setCargando]   = useState(false);
  const [error,      setError]      = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [detalle, resumenData] = await Promise.all([
        api.getRegistrosMesDetalle(anio, mes, idCp),
        api.getResumenMes(anio, mes, idCp),
      ]);
      setRegistros(detalle);
      setResumen(resumenData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al cargar resumen');
    } finally {
      setCargando(false);
    }
  }, [anio, mes, idCp]);

  useEffect(() => { cargar(); }, [cargar]);

  return { registros, resumen, cargando, error, recargar: cargar };
}
